// Live browser notification permission as React state.
//
// Shared by the Settings card and the "turn on desktop notifications" banner
// so both react the moment permission changes (e.g. the person allows it from
// the address-bar lock icon). Some browsers fire a Permissions API `change`
// event; the rest are caught by re-reading on focus / tab visibility.

import { useCallback, useEffect, useState } from 'react';
import type { BrowserPermission } from '@/lib/desktop-notification-banner';

export function readBrowserPermission(): BrowserPermission {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission as BrowserPermission;
}

export function useBrowserNotificationPermission(): [
  BrowserPermission,
  (next: BrowserPermission) => void,
] {
  const [permission, setPermission] = useState<BrowserPermission>(() => readBrowserPermission());

  useEffect(() => {
    let cancelled = false;
    let permStatus: PermissionStatus | undefined;

    function refresh() {
      if (!cancelled) setPermission(readBrowserPermission());
    }

    if ('permissions' in navigator && navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'notifications' as PermissionName })
        .then((status) => {
          if (cancelled) return;
          permStatus = status;
          refresh();
          status.onchange = refresh;
        })
        .catch(() => {
          // Some browsers reject "notifications" — the focus/visibility
          // listeners below still catch the change.
        });
    }

    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      cancelled = true;
      if (permStatus) permStatus.onchange = null;
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);

  const set = useCallback((next: BrowserPermission) => setPermission(next), []);
  return [permission, set];
}
